import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, Search, Filter, Zap, Eye, MessageSquare,
  Code, Sparkles, RefreshCw, ExternalLink, ChevronDown, ChevronRight,
} from 'lucide-react';
import { modelIntelligence, type LandscapeModel } from '../services/adminModelIntelligenceService';

const VENDOR_STYLES: Record<string, { bg: string; text: string; border: string; accent: string }> = {
  openai:     { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', accent: 'bg-emerald-500' },
  anthropic:  { bg: 'bg-violet-50', text: 'text-violet-800', border: 'border-violet-200', accent: 'bg-violet-500' },
  google:     { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', accent: 'bg-blue-500' },
  meta:       { bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200', accent: 'bg-sky-500' },
  xai:        { bg: 'bg-zinc-50', text: 'text-zinc-800', border: 'border-zinc-200', accent: 'bg-zinc-500' },
  perplexity: { bg: 'bg-teal-50', text: 'text-teal-800', border: 'border-teal-200', accent: 'bg-teal-500' },
  mistral:    { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200', accent: 'bg-orange-500' },
  cohere:     { bg: 'bg-pink-50', text: 'text-pink-800', border: 'border-pink-200', accent: 'bg-pink-500' },
  deepseek:   { bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200', accent: 'bg-indigo-500' },
};

function getVendorStyle(vendor: string) {
  return VENDOR_STYLES[vendor] || VENDOR_STYLES.openai;
}

function formatPrice(price: number | null): string {
  if (price == null) return '—';
  if (price < 0.01) return `$${price.toFixed(4)}`;
  if (price < 1) return `$${price.toFixed(2)}`;
  return `$${price.toFixed(2)}`;
}

function CapabilityChip({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
      active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
    }`}>
      {label}
    </span>
  );
}

export function AdminModelLibrary() {
  const [models, setModels] = useState<LandscapeModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [capFilter, setCapFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await modelIntelligence.getLandscape(vendorFilter || undefined);
      setModels(data.models);
    } catch (e) {
      console.error('Failed to load models:', e);
    } finally {
      setLoading(false);
    }
  }, [vendorFilter]);

  useEffect(() => { load(); }, [load]);

  const vendors = [...new Set(models.map(m => m.vendor))].sort();

  const filtered = models.filter(m => {
    if (searchTerm && !m.model_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !m.display_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (capFilter === 'reasoning' && !m.supports_reasoning) return false;
    if (capFilter === 'structured' && !m.supports_structured_outputs) return false;
    if (capFilter === 'vision' && !m.supports_vision) return false;
    return true;
  });

  const grouped = filtered.reduce((acc, m) => {
    (acc[m.vendor] = acc[m.vendor] || []).push(m);
    return acc;
  }, {} as Record<string, LandscapeModel[]>);

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-7 h-7 text-emerald-600" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>Model Library</h1>
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {models.length} models
          </span>
        </div>
        <p className="text-sm text-gray-500">
          Browse and explore all available LLM models across providers. Compare pricing, capabilities, and performance.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search models..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        {/* Vendor filter */}
        <div className="flex gap-1.5">
          <button
            onClick={() => setVendorFilter('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              !vendorFilter ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >All</button>
          {vendors.map(v => (
            <button
              key={v}
              onClick={() => setVendorFilter(vendorFilter === v ? '' : v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                vendorFilter === v
                  ? `${getVendorStyle(v).bg} ${getVendorStyle(v).text}`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >{v}</button>
          ))}
        </div>

        {/* Capability filter */}
        <div className="flex gap-1.5">
          <button onClick={() => setCapFilter('')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${!capFilter ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            <Filter className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setCapFilter(capFilter === 'reasoning' ? '' : 'reasoning')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${capFilter === 'reasoning' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            <Sparkles className="w-3.5 h-3.5 inline mr-1" />Reasoning
          </button>
          <button onClick={() => setCapFilter(capFilter === 'structured' ? '' : 'structured')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${capFilter === 'structured' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            <Code className="w-3.5 h-3.5 inline mr-1" />Structured
          </button>
          <button onClick={() => setCapFilter(capFilter === 'vision' ? '' : 'vision')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${capFilter === 'vision' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            <Eye className="w-3.5 h-3.5 inline mr-1" />Vision
          </button>
        </div>
      </div>

      {/* Model Grid by Vendor */}
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([vendor, vendorModels]) => {
        const vs = getVendorStyle(vendor);
        return (
          <div key={vendor} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${vs.accent}`} />
              <h2 className="text-lg font-semibold text-gray-900 capitalize">{vendor}</h2>
              <span className="text-xs text-gray-400">{vendorModels.length} model{vendorModels.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vendorModels.map(model => {
                const isExpanded = expanded.has(model.id);
                return (
                  <div
                    key={model.id}
                    className={`bg-white rounded-xl border ${vs.border} overflow-hidden hover:shadow-md transition-all cursor-pointer`}
                    onClick={() => toggleExpand(model.id)}
                  >
                    {/* Card Header */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{model.display_name || model.model_name}</h3>
                          <p className="text-xs font-mono text-gray-500">{model.model_name}</p>
                        </div>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                      </div>

                      {/* Pricing */}
                      <div className="flex gap-4 mb-3">
                        <div>
                          <div className="text-xs text-gray-500">Input</div>
                          <div className="text-sm font-semibold text-gray-900">{formatPrice(model.input_cost_per_mtok)}<span className="text-xs text-gray-400 font-normal">/Mtok</span></div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Output</div>
                          <div className="text-sm font-semibold text-gray-900">{formatPrice(model.output_cost_per_mtok)}<span className="text-xs text-gray-400 font-normal">/Mtok</span></div>
                        </div>
                        {model.tokens_per_second && (
                          <div>
                            <div className="text-xs text-gray-500">Speed</div>
                            <div className="text-sm font-semibold text-gray-900">{model.tokens_per_second}<span className="text-xs text-gray-400 font-normal"> tok/s</span></div>
                          </div>
                        )}
                      </div>

                      {/* Capabilities */}
                      <div className="flex flex-wrap gap-1">
                        <CapabilityChip label="Streaming" active={model.supports_streaming ?? false} />
                        <CapabilityChip label="Reasoning" active={model.supports_reasoning ?? false} />
                        <CapabilityChip label="Structured" active={model.supports_structured_outputs ?? false} />
                        <CapabilityChip label="Vision" active={model.supports_vision ?? false} />
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className={`border-t ${vs.border} ${vs.bg} p-4 space-y-3`}>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div><span className="text-gray-500">Context Window</span><br/><span className="font-medium">{model.max_input_tokens ? `${(model.max_input_tokens / 1000).toFixed(0)}K` : '—'}</span></div>
                          <div><span className="text-gray-500">Max Output</span><br/><span className="font-medium">{model.max_output_tokens ? `${(model.max_output_tokens / 1000).toFixed(0)}K` : '—'}</span></div>
                          <div><span className="text-gray-500">TTFT</span><br/><span className="font-medium">{model.ttft_seconds ? `${model.ttft_seconds}s` : '—'}</span></div>
                          <div><span className="text-gray-500">Family</span><br/><span className="font-medium">{model.model_family || '—'}</span></div>
                        </div>

                        <div className="flex flex-wrap gap-1 text-xs">
                          <CapabilityChip label="JSON Mode" active={(model as any).supports_json_mode ?? false} />
                          <CapabilityChip label="Functions" active={(model as any).supports_function_calling ?? false} />
                        </div>

                        {(model as any).notes && (
                          <p className="text-xs text-gray-600 italic">{(model as any).notes}</p>
                        )}

                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${model.status === 'available' ? 'bg-emerald-500' : model.status === 'deprecated' ? 'bg-red-400' : 'bg-amber-400'}`} />
                          {model.status}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="w-10 h-10 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No models found</h3>
          <p className="text-sm text-gray-500">Try adjusting your filters or search term.</p>
        </div>
      )}
    </div>
  );
}
